import type { RequestTypeEnum, Suggestion } from "@nlc/shared-types";

import { useNavigate } from "@tanstack/react-router";

import { Button } from "@/components/button";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import { useUser } from "@/hooks/useUser";
import { useWhitelabel } from "@/hooks/useWhitelabel";

interface SuggestionCardProps {
  data: Suggestion;
}

export function SuggestionCard({ data }: SuggestionCardProps) {
  const navigate = useNavigate();
  const user = useUser();
  const { data: request } = useGetRequest();
  const { isWhitelabel } = useWhitelabel();
  const imageUrl = isWhitelabel
    ? (data.whitelabelCoverImageUrl ?? data.coverImageUrl)
    : data.coverImageUrl;

  const handleStartNow = () => {
    navigate({
      search: {
        designType: data.designTypeId,
        "project-id": user.currentProject?.id,
        "request-type": data.requestType as RequestTypeEnum,
        ...(request?.name ? { name: request.name } : {}),
        ...(request?.brandId ? { brand_id: request.brandId } : {}),
      },
      to: "/requests/create",
    });
  };

  const handleMoreOptions = () => {
    navigate({
      reloadDocument: true,
      search: {
        category:
          data.designCategory === "graphic" || data.designCategory === "video"
            ? data.designCategory
            : "webui",
        "project-id": user.currentProject?.id,
        subcategory: data.designSubCategory,
      },
      to: "/requests/pick-type",
    });
  };

  return (
    <div className="flex flex-1 flex-col gap-xl bg-bg-primary rounded-sm p-lg shadow-lg">
      <div className="flex md:flex-col gap-3 md:gap-2 flex-1">
        <img
          alt={data.description}
          className="w-[40%] md:w-full h-full object-cover rounded-md"
          src={imageUrl}
        />
        <div className="flex flex-col justify-between flex-1">
          <div className="flex flex-col gap-2">
            <h3 className="text-text-primary text-xl font-semibold h-12 flex items-start">
              {data.designTypeName}
            </h3>
            <p className="text-text-primary font-normal text-xs">
              {data.description}
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex  md:flex-col gap-3">
        <Button
          className="flex-1 border-none"
          color="color"
          onPress={handleMoreOptions}
          size="sm"
          variant="secondary"
        >
          More options
        </Button>
        <Button
          className="flex-1"
          color="color"
          onPress={handleStartNow}
          size="sm"
          variant="primary"
        >
          Start now!
        </Button>
      </div>
    </div>
  );
}
